import {
  rewriteStyleTagCssForOpenCanvas,
  rewriteViewportUnits,
} from './rewriteViewportUnits';

/**
 * Combined fast-path: short-circuit when the input contains neither a
 * viewport-unit substring nor a `style` substring (the latter covers both
 * `style=` attributes and `<style>` elements). Intentionally over-permissive;
 * correctness comes from the downstream HTML parser and the css-tree-based
 * rewriters, not this regex.
 */
const HAS_STYLE_OR_VIEWPORT_UNIT =
  /style|(?:^|[^a-z_-])(?:s|d|l)?v(?:h|w|min|max)\b/i;

/**
 * Rewrites viewport-relative length units (vh/vw/vmin/vmax and the s/d/l
 * variants) inside an HTML string — both inline `style` attributes and
 * `<style>` element contents — so values authored in Open Canvas HTML
 * insertion paths (page-settings custom code, HTML embeds) resolve
 * against the active frame rather than the iframe viewport.
 *
 * Operates on the raw HTML string because one call site uses
 * `dangerouslySetInnerHTML`. Parses via a `<template>` element so that
 * top-level `<style>` blocks aren't hoisted into a synthesized `<head>`,
 * which would corrupt self-contained third-party widgets.
 *
 * Empty / non-HTML / malformed input is returned unchanged.
 */
export function rewriteViewportUnitsInHtml(html: string): string {
  if (!HAS_STYLE_OR_VIEWPORT_UNIT.test(html)) return html;

  try {
    const template = document.createElement('template');
    template.innerHTML = html;
    const root = template.content;

    let modified = false;

    for (const el of Array.from(
      root.querySelectorAll<HTMLElement>('[style]')
    )) {
      const cssText = el.getAttribute('style') || '';
      if (cssText === '') continue;
      const rewritten = rewriteViewportUnits(cssText, 'declarationList');
      if (rewritten !== cssText) {
        el.setAttribute('style', rewritten);
        modified = true;
      }
    }

    for (const styleEl of Array.from(root.querySelectorAll('style'))) {
      const cssText = styleEl.textContent || '';
      if (cssText === '') continue;
      const rewritten = rewriteStyleTagCssForOpenCanvas(cssText);
      if (rewritten !== cssText) {
        styleEl.textContent = rewritten;
        modified = true;
      }
    }

    if (!modified) return html;
    return template.innerHTML;
  } catch (_error) {
    return html;
  }
}
